import { mainApi } from '../../../config/apiClient';

// ===================== Türler =====================

export type CourierKind = 'OWN' | 'FREELANCE' | 'COMPANY' | 'PLATFORM_VALET' | 'INTEGRATION';
export type ValetProvider = 'YEMEKSEPETI' | 'UBER' | 'MIGROS';

export const VALET_PROVIDERS: ValetProvider[] = ['YEMEKSEPETI', 'UBER', 'MIGROS'];

export interface CourierProfile {
  id: number;
  firebaseUid: string;
  kind: CourierKind;
  providerCode?: string | null;
  name?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  iban?: string | null;
  plateNumber?: string | null;
  notes?: string | null;
  branchIds: string[];
  accountId?: number | null;
  parentCourierId?: number | null;
  parentCourierName?: string | null;
  usesApp: boolean;
  collectsCash: boolean;
  paidFromCash: boolean;
  status: string;
  isActive: boolean;
  agreementSummary?: string | null;
  agreementVersion?: number | null;
  assignable: boolean;
  notAssignableReason?: string | null;
}

export interface CourierProfileRequest {
  kind: CourierKind;
  providerCode?: string | null;
  name?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  iban?: string | null;
  plateNumber?: string | null;
  notes?: string | null;
  branchIds: string[];
  accountId?: number | null;
  parentCourierId?: number | null;
  usesApp: boolean;
  firebaseUid?: string | null;
  collectsCash: boolean;
  paidFromCash: boolean;
  packageOrders?: boolean;
  restaurantOrders?: boolean;
  ecommerceOrders?: boolean;
}

export interface Surcharge {
  percent?: number | null;
  amount?: number | null;
}

export interface NightSurcharge extends Surcharge {
  start: string;
  end: string;
}

export interface ZoneSurcharge extends Surcharge {
  zoneId: number;
}

export type CancelRule = 'NEVER' | 'IF_PICKED_UP' | 'ALWAYS';
export type SettlementPeriod = 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';

export interface CourierTerms {
  perPackageFee?: number | null;
  zonePricing: boolean;
  zonePrices: { zoneId: number; price: number }[];
  unmatchedZoneFee?: number | null;
  hourlyRate?: number | null;
  dailyFixed?: number | null;
  monthlyFixed?: number | null;
  dailyGuarantee?: number | null;
  night?: NightSurcharge | null;
  weekend?: Surcharge | null;
  badWeather?: Surcharge | null;
  zoneSurcharges: ZoneSurcharge[];
  cancelRule: CancelRule;
  vatIncluded: boolean;
  /** KDV oranı (%); fiyatlar KDV hariçse hakedişte eklenir. Boşsa %20. */
  vatRate?: number | null;
  settlementPeriod: SettlementPeriod;
}

export const emptyTerms = (): CourierTerms => ({
  perPackageFee: null,
  zonePricing: false,
  zonePrices: [],
  unmatchedZoneFee: null,
  hourlyRate: null,
  dailyFixed: null,
  monthlyFixed: null,
  dailyGuarantee: null,
  night: null,
  weekend: null,
  badWeather: null,
  zoneSurcharges: [],
  cancelRule: 'IF_PICKED_UP',
  vatIncluded: true,
  settlementPeriod: 'DAILY',
});

export interface CourierAgreement {
  id: number;
  courierId: number;
  version: number;
  validFrom: string;
  summary?: string | null;
  terms: CourierTerms;
  createdAt: string;
  createdBy?: string | null;
  isCurrent: boolean;
}

export interface FeeLine {
  code: string;
  label: string;
  amount: number;
}

export interface FeeResult {
  lines: FeeLine[];
  total: number;
  zoneMissing: boolean;
}

export interface CourierZone {
  id: number;
  branchId: string;
  name: string;
  neighborhoods: string[];
  sortOrder: number;
}

export interface NeighborhoodSuggestion {
  name: string;
  orderCount: number;
}

// ===================== Kurye paneli =====================

export interface CourierPanelRow {
  id: number;
  firebaseUid: string;
  name?: string | null;
  kind: CourierKind;
  providerCode?: string | null;
  phoneNumber?: string | null;
  usesApp: boolean;
  collectsCash: boolean;
  parentCourierName?: string | null;
  agreementSummary?: string | null;
  state: 'available' | 'on_road';
  inHandCount: number;
  todayDelivered: number;
  todayFee: number;
  todayCashExpected: number;
  /** Bugün yazılan nakit tahsilatlar. */
  todayCashCollected: number;
  /** Kuryedeki nakit (kurye kasası + başka kasaya yazılmış nakit − gün içi teslimler); nakit taşımayanda null. */
  cashOnHand?: number | null;
  hasCashbox?: boolean;
  /** Kasiyer onayı bekleyen nakit teslim isteği. */
  pendingHandoverId?: number | null;
  pendingHandoverAmount?: number | null;
  pendingHandoverAt?: string | null;
  /** Faturalanamayan kurye ödemesi sayısı. */
  failedCollections: number;
  lastDeliveredAt?: string | null;
  queueNo?: number | null;
  assignable: boolean;
  notAssignableReason?: string | null;
  /** Kurye uygulamasının son gönderdiği konumun zamanı (UTC). */
  locationAt?: string | null;
  hasLocation?: boolean;
  /** ok | denied | service_off | null */
  locationState?: string | null;
  /** Haritada gösterilebilir (uygulamayı kullanan kendi kuryesi). */
  mappable?: boolean;
}

export interface CourierMapBranch {
  branchId: string;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface CourierMapOrder {
  activeOrderId: number;
  orderNumber: string;
  customerName?: string | null;
  deliveryAddress?: string | null;
  status: string;
  latitude?: number | null;
  longitude?: number | null;
  /** Kuryeden teslim adresine kuş uçuşu (km). */
  distanceKm?: number | null;
}

export interface CourierMapCourier {
  id: number;
  name?: string | null;
  phoneNumber?: string | null;
  state: 'available' | 'on_road' | string;
  latitude?: number | null;
  longitude?: number | null;
  locationAt?: string | null;
  locationState?: string | null;
  locationStateAt?: string | null;
  nudgedAt?: string | null;
  restaurantBranchId?: string | null;
  /** Kuryeden restorana kuş uçuşu (km). */
  restaurantDistanceKm?: number | null;
  orders: CourierMapOrder[];
}

export interface CourierMap {
  branches: CourierMapBranch[];
  couriers: CourierMapCourier[];
  serverTime: string;
}

export interface CourierInHandOrder {
  activeOrderId: number;
  orderSource: string;
  orderRef: number;
  orderNumber: string;
  customerName?: string | null;
  deliveryAddress?: string | null;
  status: string;
  paymentMethod?: string | null;
  totalAmount?: number | null;
  assignedAt: string;
  pickedUpAt?: string | null;
  partNo: number;
  partCount: number;
}

export interface CourierDeliveryRow {
  id: number;
  orderSource: string;
  orderRef: number;
  orderNumber?: string | null;
  customerName?: string | null;
  neighborhood?: string | null;
  zoneName?: string | null;
  zoneManual: boolean;
  expectedPayment: string;
  orderAmount?: number | null;
  status: string;
  assignedAt: string;
  pickedUpAt?: string | null;
  deliveredAt?: string | null;
  feeTotal: number;
  feeManual: boolean;
  feeFinal: boolean;
  partNo: number;
  partCount: number;
  partNote?: string | null;
  collectsPayment: boolean;
  feeLines: FeeLine[];
}

export interface CourierDay {
  courier: CourierPanelRow;
  inHand: CourierInHandOrder[];
  deliveries: CourierDeliveryRow[];
  deliveredCount: number;
  feeTotal: number;
  cashExpected: number;
  cardExpected: number;
  onlineTotal: number;
  averageDeliveryMinutes?: number | null;
  monthFee: number;
}

export interface QuoteOrderRef {
  source: string;
  orderRef: number;
  zoneId?: number | null;
}

export interface QuoteOrder {
  source: string;
  orderRef: number;
  orderNumber?: string | null;
  customerName?: string | null;
  neighborhood?: string | null;
  zoneId?: number | null;
  zoneName?: string | null;
  zoneMissing: boolean;
  expectedPayment: string;
  amount?: number | null;
}

export interface QuoteForCourier {
  courierId: number;
  fees: number[];
  total: number;
  eligible: boolean;
  reason?: string | null;
}

export interface QuoteResult {
  orders: QuoteOrder[];
  couriers: QuoteForCourier[];
  zones: CourierZone[];
}

/** Paket ekranındaki kaynak → sunucudaki kaynak (telefon/paket = package). */
// ===================== Kurye çağır =====================

export type CourierCallAnswerKind = 'pending' | 'coming' | 'declined';

export interface CourierCallAnswer {
  courierId: number;
  courierName?: string | null;
  answer: CourierCallAnswerKind;
  etaMinutes?: number | null;
  respondedAt?: string | null;
}

export interface CourierCall {
  id: number;
  branchId?: string | null;
  orderSource?: string | null;
  orderRef?: number | null;
  orderNumber?: string | null;
  neighborhood?: string | null;
  message?: string | null;
  /** open: cevap bekleniyor, taken: bir kurye geliyor (ön atama). */
  status: 'open' | 'taken' | 'assigned' | 'cancelled' | 'expired';
  createdAt: string;
  expiresAt: string;
  createdBy?: string | null;
  acceptedCourierId?: number | null;
  acceptedCourierName?: string | null;
  acceptedAt?: string | null;
  answers: CourierCallAnswer[];
}

export interface CourierCallCreate {
  branchId?: string | null;
  orderSource?: string | null;
  orderRef?: number | null;
  orderNumber?: string | null;
  /** Boşsa şubede uygulamayı kullanan tüm kuryeler. */
  courierIds?: number[] | null;
  message?: string | null;
}

/** Kurye uygulamasını kullanan (çağrılabilir) kurye mi? */
export const isCallable = (r: { firebaseUid: string; usesApp: boolean; kind: string }) =>
  r.usesApp && !r.firebaseUid.startsWith('ext_') && r.kind !== 'PLATFORM_VALET' && r.kind !== 'INTEGRATION';

// ===================== Ödeme ve kurye kasası =====================

export interface CourierBranchSetting {
  branchId: string;
  courierCashboxEnabled: boolean;
  settlementCashboxId?: number | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface CourierPaymentDraft {
  branchId?: string | null;
  orderSource: string;
  orderRef: number;
  /** "Ödeme yap"ın nakit isteği (PascalCase). */
  payload: any;
  cashboxId?: number | null;
  cashboxName?: string | null;
  posBankAccountId?: number | null;
  posBankSubAccountId?: number | null;
  posBankAccountName?: string | null;
  pavoBankAccountId?: number | null;
  pavoBankSubAccountId?: number | null;
  pavoBankAccountName?: string | null;
}

export type CollectionMethod = 'CASH' | 'POS' | 'PAVO' | 'MIXED';

export interface CourierCollection {
  id: number;
  courierId: number;
  courierName?: string | null;
  orderSource: string;
  orderRef: number;
  orderNumber?: string | null;
  method: CollectionMethod;
  cashAmount: number;
  cardAmount: number;
  origin: 'app' | 'web';
  /** processed: faturalandı, recorded: kasiyer faturalayacak, failed: faturalanamadı. */
  status: 'pending' | 'processed' | 'failed' | 'recorded';
  invoiceNumber?: string | null;
  error?: string | null;
  createdAt: string;
  /** Ödeme türü düzeltildiyse eski tür (ör. CASH). */
  correctedFrom?: string | null;
  correctedBy?: string | null;
  correctedAt?: string | null;
  settlementId?: number | null;
}

/** Gün sonu ekranında kuryenin açık (hesaplaşılmamış) toplamları. */
export interface CourierSettlementRow {
  courierId: number;
  name?: string | null;
  kind: CourierKind;
  usesApp: boolean;
  paidFromCash: boolean;
  packageCount: number;
  cashInBox?: number | null;
  courierCashboxId?: number | null;
  courierCashboxName?: string | null;
  cashElsewhere: number;
  carriedIn: number;
  cashSystem: number;
  cardSystem: number;
  pavoSystem: number;
  feePackages: number;
  feeExtras: number;
  feeTotal: number;
  blockingCount: number;
  /** Gün içinde teslim alınan nakit (sistemdeki nakitten düşülmüş). */
  intradayHandedOver: number;
  /** Bekleyen nakit teslim isteği (kapatmayı engeller). */
  hasPendingHandover: boolean;
  collections: CourierCollection[];
}

export interface CourierSettlement {
  id: number;
  courierId: number;
  courierName?: string | null;
  businessDate: string;
  packageCount: number;
  cashSystem: number;
  cashCounted: number;
  cashDifference: number;
  cardSystem: number;
  pavoSystem: number;
  cardSlip?: number | null;
  feeTotal: number;
  feeFromCash: number;
  handedOver: number;
  intradayHandedOver?: number;
  targetCashboxName?: string | null;
  status: 'open' | 'closed' | 'failed';
  note?: string | null;
  error?: string | null;
  createdBy?: string | null;
  createdAt: string;
}

export interface CourierSettlementDay {
  date: string;
  branchId?: string | null;
  courierCashboxEnabled: boolean;
  settlementCashboxId?: number | null;
  rows: CourierSettlementRow[];
  closed: CourierSettlement[];
}

// ===================== Hakediş =====================

export type CourierPayoutStatus = 'pending' | 'approved' | 'paid' | 'cancelled';
export type CourierPayoutMethod = 'cash' | 'bank' | 'deduction';

export interface CourierPayoutRequest {
  courierId: number;
  branchId?: string | null;
  periodStart: string;
  periodEnd: string;
  offsetCashDebt: boolean;
  note?: string | null;
}

export interface CourierPayoutBranchLine {
  branchId?: string | null;
  branchName?: string | null;
  packages: number;
  cancelledPaid: number;
  fee: number;
}

export interface CourierPayoutPayment {
  at: string;
  amount: number;
  method: CourierPayoutMethod;
  sourceName?: string | null;
  by?: string | null;
  note?: string | null;
}

export interface CourierPayoutDeliveryRow {
  id: number;
  at?: string | null;
  branchId?: string | null;
  courierName?: string | null;
  orderSource: string;
  orderNumber?: string | null;
  zoneName?: string | null;
  neighborhood?: string | null;
  status: string;
  fee: number;
}

export interface CourierPayout {
  id: number;
  courierId: number;
  courierName?: string | null;
  courierKind: CourierKind;
  branchId?: string | null;
  periodStart: string;
  periodEnd: string;
  packageCount: number;
  cancelledPaidCount: number;
  feePackages: number;
  feeExtras: number;
  feeNet: number;
  vatIncluded: boolean;
  vatRate: number;
  vatAmount: number;
  total: number;
  feeFromCash: number;
  cashOffset: number;
  cashDebt: number;
  payable: number;
  paid: number;
  remaining: number;
  accountId?: number | null;
  accountName?: string | null;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  invoiceTotal?: number | null;
  status: CourierPayoutStatus;
  note?: string | null;
  createdBy?: string | null;
  createdAt: string;
  approvedBy?: string | null;
  approvedAt?: string | null;
  paidAt?: string | null;
  branches: CourierPayoutBranchLine[];
  payments: CourierPayoutPayment[];
  deliveries: CourierPayoutDeliveryRow[];
}

export interface CourierPayoutCandidate {
  courierId: number;
  name?: string | null;
  kind: CourierKind;
  accountId?: number | null;
  settlementPeriod: SettlementPeriod;
  lastPeriodEnd?: string | null;
  suggestedStart: string;
  suggestedEnd: string;
  openPackages: number;
  openFees: number;
  openPayouts: number;
  openPayable: number;
}

export interface CourierPayoutInvoiceOption {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  grandTotal: number;
  status?: string | null;
}

export interface CourierPayoutPay {
  method: CourierPayoutMethod;
  cashboxId?: number | null;
  bankAccountId?: number | null;
  amount?: number | null;
  note?: string | null;
}

// ===================== Fiyuu =====================

export interface FiyuuSendDraft {
  connectionReady: boolean;
  connectionProblem?: string | null;
  storeId?: string | null;
  environment: 'test' | 'live';
  orderSource: string;
  orderRef: number;
  orderNumber?: string | null;
  firstName: string;
  lastName: string;
  phone: string;
  extensionPhone?: string | null;
  city: string;
  county: string;
  neighborhood: string;
  street: string;
  buildingName?: string | null;
  buildingNumber: string;
  floor?: string | null;
  doorNumber: string;
  fullAddress: string;
  addressDirection?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  paymentType: string;
  isPaid: boolean;
  totalAmount: number;
  note?: string | null;
  previousExternalOrderId?: string | null;
}

export interface FiyuuSendRequest {
  branchId: string;
  orderSource: string;
  orderRef: number;
  partNo: number;
  partCount: number;
  firstName: string;
  lastName: string;
  phone: string;
  extensionPhone?: string | null;
  city: string;
  county: string;
  neighborhood: string;
  street: string;
  buildingName?: string | null;
  buildingNumber: string;
  floor?: string | null;
  doorNumber: string;
  fullAddress?: string | null;
  addressDirection?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  paymentType: string;
  totalAmount: number;
  note?: string | null;
}

export const isFiyuuCourier = (r?: { kind?: string; providerCode?: string | null } | null): boolean =>
  !!r && r.kind === 'INTEGRATION' && (r.providerCode || '').toUpperCase() === 'FIYUU';

// ===================== Kurye raporu =====================

export type CourierReportGroup = 'kind' | 'courier' | 'branch' | 'source';

export interface CourierReportRow {
  key: string;
  label?: string | null;
  kind?: CourierKind | null;
  packages: number;
  cancelledPaid: number;
  fee: number;
  perPackage?: number | null;
  orderAmount: number;
  feeRatio?: number | null;
  avgDeliveryMinutes?: number | null;
}

export interface CourierReport {
  from: string;
  to: string;
  branchId?: string | null;
  groupBy: CourierReportGroup;
  rows: CourierReportRow[];
  total: CourierReportRow;
}

// ===================== Gün içi nakit teslimi =====================

export type CourierCashHandoverStatus = 'pending' | 'processing' | 'received' | 'rejected' | 'withdrawn' | 'failed';

export interface CourierCashHandover {
  id: number;
  courierId: number;
  courierName?: string | null;
  branchId?: string | null;
  source: 'courier' | 'panel';
  requestedAmount?: number | null;
  requestedAll: boolean;
  courierNote?: string | null;
  requestedAt?: string | null;
  status: CourierCashHandoverStatus;
  receivedAmount?: number | null;
  targetCashboxName?: string | null;
  resolvedBy?: string | null;
  resolvedAt?: string | null;
  note?: string | null;
  settled: boolean;
  createdAt: string;
}

export interface CourierCashHandoverState {
  courierId: number;
  courierName?: string | null;
  cashOnHand: number;
  hasCourierCashbox: boolean;
  defaultCashboxId?: number | null;
  pending?: CourierCashHandover | null;
  today: CourierCashHandover[];
  todayReceived: number;
}

export interface CourierCashReceive {
  courierId: number;
  handoverId?: number | null;
  branchId?: string | null;
  all: boolean;
  amount?: number | null;
  targetCashboxId?: number | null;
  note?: string | null;
}

export interface CourierSettlementClose {
  courierId: number;
  branchId?: string | null;
  date?: string | null;
  countedCash: number;
  cardSlip?: number | null;
  payFeeFromCash: boolean;
  targetCashboxId?: number | null;
  note?: string | null;
}

/**
 * Sayılan nakit eksikse, eksik tutarında nakit yazılmış siparişler (kartla ödenmiş olabilir).
 * POS fişi yazıldıysa ve fazla da aynı tutardaysa öneri güçlenir.
 */
export const suggestMismatch = (row: CourierSettlementRow, counted: number, slip?: number | null): CourierCollection[] => {
  const shortage = Math.round((row.cashSystem - counted) * 100) / 100;
  if (shortage <= 0) return [];
  if (slip != null && Math.round((slip - row.cardSystem) * 100) / 100 !== shortage) return [];
  return row.collections.filter((c) => c.method === 'CASH' && Math.abs(c.cashAmount - shortage) < 0.005);
};

export const toApiSource = (source?: string | null): string =>
  source === 'getir' || source === 'migros' || source === 'trendyol' || source === 'yemeksepeti' ? source : 'package';

// ===================== API =====================

const BASE = '/api/CourierModule';

/** Sunucunun "success: false" mesajını hata olarak fırlatır. */
async function unwrap<T>(promise: Promise<any>): Promise<T> {
  try {
    const res = await promise;
    if (res.data?.success === false) throw new Error(res.data?.message || 'İşlem yapılamadı');
    return res.data?.data as T;
  } catch (err: any) {
    const message = err?.response?.data?.message;
    throw new Error(message || err?.message || 'İşlem yapılamadı');
  }
}

export const courierModuleService = {
  listCouriers: () => unwrap<CourierProfile[]>(mainApi.get(`${BASE}/couriers`)),
  createCourier: (req: CourierProfileRequest) => unwrap<CourierProfile>(mainApi.post(`${BASE}/couriers`, req)),
  updateCourier: (id: number, req: CourierProfileRequest) => unwrap<CourierProfile>(mainApi.post(`${BASE}/couriers/${id}`, req)),
  valetBranches: () => unwrap<Record<ValetProvider, string[]>>(mainApi.get(`${BASE}/valet-branches`)),

  listAgreements: (courierId: number) => unwrap<CourierAgreement[]>(mainApi.get(`${BASE}/couriers/${courierId}/agreements`)),
  addAgreement: (courierId: number, terms: CourierTerms, validFrom?: string | null) =>
    unwrap<CourierAgreement>(mainApi.post(`${BASE}/couriers/${courierId}/agreements`, { terms, validFrom: validFrom || null })),
  feePreview: (body: { terms: CourierTerms; zoneId?: number | null; localTime?: string | null; badWeather?: boolean }) =>
    unwrap<FeeResult>(mainApi.post(`${BASE}/fee-preview`, body)),

  listZones: (branchId?: string) =>
    unwrap<CourierZone[]>(mainApi.get(`${BASE}/zones`, { params: branchId ? { branchId } : undefined })),
  saveZone: (zone: { id?: number | null; branchId: string; name: string; neighborhoods: string[]; sortOrder: number }) =>
    unwrap<CourierZone>(mainApi.post(`${BASE}/zones`, zone)),
  deleteZone: (id: number) => unwrap<boolean>(mainApi.post(`${BASE}/zones/${id}/delete`, {})),
  suggestions: (branchId: string) =>
    unwrap<NeighborhoodSuggestion[]>(mainApi.get(`${BASE}/zones/suggestions`, { params: { branchId } })),

  panel: (branchId?: string | null) =>
    unwrap<CourierPanelRow[]>(mainApi.get(`${BASE}/panel`, { params: branchId ? { branchId } : undefined })),
  courierDay: (courierId: number, date?: string) =>
    unwrap<CourierDay>(mainApi.get(`${BASE}/couriers/${courierId}/day`, { params: date ? { date } : undefined })),
  quote: (branchId: string, orders: QuoteOrderRef[]) =>
    unwrap<QuoteResult>(mainApi.post(`${BASE}/quote`, { branchId, orders })),
  learnNeighborhood: (branchId: string, zoneId: number, neighborhood: string) =>
    unwrap<CourierZone>(mainApi.post(`${BASE}/zones/learn`, { branchId, zoneId, neighborhood })),
  settings: () => unwrap<CourierBranchSetting[]>(mainApi.get(`${BASE}/settings`)),
  saveSetting: (s: { branchId: string; courierCashboxEnabled: boolean; settlementCashboxId?: number | null }) =>
    unwrap<CourierBranchSetting>(mainApi.post(`${BASE}/settings`, s)),
  savePaymentDraft: (draft: CourierPaymentDraft) => unwrap<boolean>(mainApi.post(`${BASE}/payment-drafts`, draft)),
  collections: (params: { branchId?: string | null; date?: string; courierId?: number }) =>
    unwrap<CourierCollection[]>(mainApi.get(`${BASE}/collections`, {
      params: {
        ...(params.branchId ? { branchId: params.branchId } : {}),
        ...(params.date ? { date: params.date } : {}),
        ...(params.courierId ? { courierId: params.courierId } : {}),
      },
    })),
  correctCollection: (id: number, body: { method: 'POS' | 'PAVO'; bankAccountId?: number | null; bankSubAccountId?: number | null }) =>
    unwrap<CourierCollection>(mainApi.post(`${BASE}/collections/${id}/correct`, body)),
  settlementDay: (branchId?: string | null, date?: string) =>
    unwrap<CourierSettlementDay>(mainApi.get(`${BASE}/settlements/day`, {
      params: { ...(branchId ? { branchId } : {}), ...(date ? { date } : {}) },
    })),
  closeSettlement: (req: CourierSettlementClose) => unwrap<CourierSettlement>(mainApi.post(`${BASE}/settlements/close`, req)),
  handoverState: (courierId: number, branchId?: string | null) =>
    unwrap<CourierCashHandoverState>(mainApi.get(`${BASE}/handovers/state`, {
      params: { courierId, ...(branchId ? { branchId } : {}) },
    })),
  courierMap: (p: { branchId?: string | null; courierId?: number | null }) =>
    unwrap<CourierMap>(mainApi.get(`${BASE}/map`, {
      params: { ...(p.branchId ? { branchId: p.branchId } : {}), ...(p.courierId ? { courierId: p.courierId } : {}) },
    })),
  nudgeLocation: (courierId: number) =>
    unwrap<{ sentAt: string; courierName?: string | null }>(mainApi.post(`${BASE}/couriers/${courierId}/location-nudge`, {})),
  receiveHandover: (req: CourierCashReceive) => unwrap<CourierCashHandover>(mainApi.post(`${BASE}/handovers/receive`, req)),
  rejectHandover: (id: number, note?: string | null) =>
    unwrap<CourierCashHandover>(mainApi.post(`${BASE}/handovers/${id}/reject`, { note: note || null })),
  report: (p: { from: string; to: string; branchId?: string | null; groupBy: CourierReportGroup }) =>
    unwrap<CourierReport>(mainApi.get(`${BASE}/report`, {
      params: { from: p.from, to: p.to, groupBy: p.groupBy, ...(p.branchId ? { branchId: p.branchId } : {}) },
    })),
  fiyuuDraft: (branchId: string, source: string, orderRef: number) =>
    unwrap<FiyuuSendDraft>(mainApi.get(`${BASE}/fiyuu/draft`, { params: { branchId, source, orderRef } })),
  fiyuuSend: (req: FiyuuSendRequest) =>
    unwrap<{ externalOrderId: string; linked: boolean }>(mainApi.post(`${BASE}/fiyuu/send`, req)),
  fiyuuLink: (externalOrderId: string) => unwrap<boolean>(mainApi.post(`${BASE}/fiyuu/link`, { externalOrderId })),
  payoutCandidates: () => unwrap<CourierPayoutCandidate[]>(mainApi.get(`${BASE}/payouts/candidates`)),
  payouts: (courierId?: number) =>
    unwrap<CourierPayout[]>(mainApi.get(`${BASE}/payouts`, { params: courierId ? { courierId } : undefined })),
  payout: (id: number) => unwrap<CourierPayout>(mainApi.get(`${BASE}/payouts/${id}`)),
  previewPayout: (req: CourierPayoutRequest) => unwrap<CourierPayout>(mainApi.post(`${BASE}/payouts/preview`, req)),
  createPayout: (req: CourierPayoutRequest) => unwrap<CourierPayout>(mainApi.post(`${BASE}/payouts`, req)),
  approvePayout: (id: number) => unwrap<CourierPayout>(mainApi.post(`${BASE}/payouts/${id}/approve`, {})),
  payoutInvoices: (id: number) => unwrap<CourierPayoutInvoiceOption[]>(mainApi.get(`${BASE}/payouts/${id}/invoices`)),
  linkPayoutInvoice: (id: number, invoiceId: string) =>
    unwrap<CourierPayout>(mainApi.post(`${BASE}/payouts/${id}/invoice`, { invoiceId })),
  unlinkPayoutInvoice: (id: number) => unwrap<CourierPayout>(mainApi.delete(`${BASE}/payouts/${id}/invoice`)),
  payPayout: (id: number, req: CourierPayoutPay) => unwrap<CourierPayout>(mainApi.post(`${BASE}/payouts/${id}/pay`, req)),
  cancelPayout: (id: number) => unwrap<CourierPayout>(mainApi.post(`${BASE}/payouts/${id}/cancel`, {})),
  retryCollection: (id: number) =>
    unwrap<{ status: string; message: string; invoiceNumber?: string | null }>(mainApi.post(`${BASE}/collections/${id}/retry`, {})),
  createCall: (req: CourierCallCreate) => unwrap<CourierCall>(mainApi.post(`${BASE}/calls`, req)),
  listCalls: (branchId?: string | null) =>
    unwrap<CourierCall[]>(mainApi.get(`${BASE}/calls`, { params: branchId ? { branchId } : undefined })),
  cancelCall: (id: number) => unwrap<boolean>(mainApi.post(`${BASE}/calls/${id}/cancel`, {})),
  /** Kuryeden siparişi geri alır (atamayı kaldırır). */
  unassign: (activeOrderId: number) =>
    mainApi.post('/api/CourierActiveOrders/cancel-assignment', { activeOrderId }).then((r) => r.data),
};

export const providerLabel = (code?: string | null): string => {
  switch (code) {
    case 'YEMEKSEPETI': return 'Yemeksepeti';
    case 'UBER': return 'Uber';
    case 'MIGROS': return 'Migros';
    case 'FIYUU': return 'Fiyuu';
    case 'ZIRVEGO': return 'Zirve Go';
    default: return code || '';
  }
};

/** Kuryedeki nakit: kurye kasası varsa bakiyesi, yoksa bugünkü tahsilat (yoksa beklenen). */
export const cashOf = (r: { cashOnHand?: number | null; todayCashCollected?: number; todayCashExpected: number }): number =>
  r.cashOnHand ?? ((r.todayCashCollected ?? 0) > 0 ? (r.todayCashCollected ?? 0) : r.todayCashExpected);

export const formatMoney = (v?: number | null): string =>
  v == null ? '—' : `${v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
